import { defineAction } from '~/prix'
import { z } from 'zod'
import insightListAction from './insightList.action.json' assert { type: 'json' }
import { ALERT_PRESET_FILTER_OPTIONS } from '../alertTypes.constants'

const normalizeStringArrayInput = (value: unknown) => {
  if (value == null) {
    return undefined
  }

  const values = Array.isArray(value) ? value : [value]

  const normalizedValues = values
    .flatMap(currentValue => String(currentValue).split(','))
    .map(currentValue => currentValue.trim())
    .filter(Boolean)

  return normalizedValues.length > 0 ? normalizedValues : undefined
}

const stringArrayInputZod = z.preprocess(
  normalizeStringArrayInput,
  z.array(z.string().trim().min(1)).optional(),
)

export const insightListBaseInputZod = z.object({
  search: z.string().trim().optional(),
  delimitation: z.string().trim().optional(),
  variableKeys: stringArrayInputZod,
  variablePresetKeys: stringArrayInputZod,
  alertType: stringArrayInputZod,
  alertPreset: z.enum(ALERT_PRESET_FILTER_OPTIONS).optional(),
  sortBy: z
    .enum(['relevance', 'description', 'state', 'alertBadge', 'alertType', 'variable'])
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  startIndex: z.number().min(0).optional(),
  endIndex: z.number().min(0).optional(),
})

export function withInsightListRangeValidation<T extends z.AnyZodObject>(schema: T) {
  return schema.refine(data => (data.endIndex ?? 50) >= (data.startIndex ?? 0), {
    message: 'endIndex must be greater than or equal to startIndex',
    path: ['endIndex'],
  })
}

export const insightListInputZod = withInsightListRangeValidation(insightListBaseInputZod)
export const inputZod = insightListInputZod

const insightListDefinition = defineAction({
  ...insightListAction,
  input: insightListInputZod,
  output: z.object({
    items: z.array(z.object({}).passthrough()),
    total: z.number(),
  }),
  cache: input => {
    if (input.search) {
      return {}
    }

    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 2,
        expirationMsForUpdate: 1000 * 60 * 5,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 5,
        expirationMsForUpdate: 1000 * 60 * 5,
      },
    }
  },
})

export default insightListDefinition
